import { useEffect, useState } from "react";
import type { Row } from "../types";
import { api, ApiError, type SugestaoEngaj } from "../api";
import { IconClose, IconSparkles, IconCheck } from "../icons";

interface Props {
  registro: Row; // linha de engajamento alvo
  onClose: () => void;
  onSaved: () => void; // recarrega a lista após salvar o plano
}

/**
 * Modal de IA: a partir dos indicadores de engajamento, sugere o que fazer
 * para engajar melhor o colaborador e salva o plano na observação do registro.
 */
export default function SugerirEngajamentoModal({ registro, onClose, onSaved }: Props) {
  const [data, setData] = useState<SugestaoEngaj | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  async function gerar() {
    setLoading(true);
    setError(null);
    setOkMsg(null);
    try {
      setData(await api.rhIa.sugerirEngajamento(registro.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao gerar sugestões");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    gerar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registro.id]);

  function planoTexto(d: SugestaoEngaj): string {
    return d.acoes
      .map((a) => `• [${a.categoria} • ${a.prazo_dias} dias] ${a.acao}`)
      .join("\n");
  }

  async function salvar() {
    if (!data) return;
    setSaving(true);
    setError(null);
    try {
      const texto = `${data.diagnostico}\n\n${planoTexto(data)}`;
      await api.rhIa.salvarEngajamento(registro.id, texto);
      setOkMsg("Plano de melhoria de clima salvo na observação do registro.");
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Falha ao salvar o plano");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card modal-ia" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>
            <IconSparkles size={18} /> Como melhorar o clima —{" "}
            {data?.engajamento.colaborador || registro.colaborador_nome || "colaborador"}
          </h3>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div className="empty">Analisando os indicadores e gerando ações…</div>
          ) : error && !data ? (
            <div className="form-error">{error}</div>
          ) : data ? (
            <>
              <div className="ia-diag">
                <span className={`ia-tag ${data.fonte === "ia" ? "ia-tag-ai" : "ia-tag-std"}`}>
                  {data.fonte === "ia" ? "IA" : "Padrão"}
                </span>
                {data.diagnostico || "Ações recomendadas para melhorar o clima do colaborador."}
              </div>

              <ul className="ia-list">
                {data.acoes.map((a, i) => (
                  <li key={i} className="ia-item ia-item-static">
                    <span className="ia-item-main">
                      <span className="ia-item-title">
                        {a.acao}
                        <span className="ia-carga">{a.prazo_dias} dias</span>
                      </span>
                      <span className="ia-item-sub">{a.categoria}</span>
                    </span>
                  </li>
                ))}
              </ul>

              {error && <div className="form-error">{error}</div>}
              {okMsg && (
                <div className="form-ok">
                  <IconCheck size={16} /> {okMsg}
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={gerar} disabled={loading || saving}>
            Regerar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={salvar}
            disabled={loading || saving || !data}
          >
            {saving ? "Salvando…" : "Salvar plano na observação"}
          </button>
        </div>
      </div>
    </div>
  );
}
